import type { CameraCalibrationMessage } from '../protocol.ts';
import { mountTvPicker } from '../tvPicker.ts';
import { solvePlanarTvPose, type FourCorners, type Point2 } from './planar.ts';
import {
  CAMERA_INSTALLATION_LABELS,
  composeInstalledCamera,
  declaredBottomCentreCamera,
  declaredStandOffset,
  flipInPlaceOffset,
  type InstalledCameraGeometry,
} from './cameraInstall.ts';
import {
  DEFAULT_CUSTOM_HORIZONTAL_FOV_DEG,
  PHONE_FRONT_CAMERAS,
  phoneHorizontalFov,
  phoneProfileMatchesFacingMode,
} from '../phoneFov.ts';
import { frameBudgetMs, isTargetFps, type FrameBudgetSnapshot, type TargetFps } from '../media/frameBudget.ts';
import type { RtcStreamMeasurement } from '../media/rtcStats.ts';
import { mountRtcStatsHud } from '../media/rtcStatsHud.ts';
import type { PublishSettingsResult } from '../media/publishSettings.ts';
import type { NormalizedPoint } from '../media/consent.ts';
import type { PackedLayout } from '../media/packed.ts';

const CORNER_NAMES = ['top left', 'top right', 'bottom right', 'bottom left'] as const;

export interface CalibrationUi {
  stop(): void;
}

export interface CalibrationUiHooks {
  onStreamStarted?: (
    stream: MediaStream,
    video: HTMLVideoElement,
    setStatus: (message: string) => void,
    setDiagnostics: (diagnostics: { posePersonCount: number; maskPersonCount: number }) => void,
    setPerformance: (snapshot: FrameBudgetSnapshot) => void,
    setRtcStats: (measurement: RtcStreamMeasurement | undefined) => void,
    setPublishSettings: (settings: PublishSettingsResult | undefined) => void,
    targetFps: TargetFps,
    packedLayout: PackedLayout,
    markGrantReady: () => void,
  ) => void;
  onGrantRequest?: (tap?: NormalizedPoint) => void;
  onTargetFpsChange?: (targetFps: TargetFps) => void;
  onInstallation?: (installation: CameraCalibrationMessage) => void;
  onStop?: () => void;
}

export function mountCalibrationUi(container: HTMLElement, hooks: CalibrationUiHooks = {}): CalibrationUi {
  container.innerHTML = `
    <section class="calibration-card">
      <div class="calibration-heading">
        <div><p class="eyebrow">CAMERA → TV FRAME</p><h2>Place the under-TV camera</h2></div>
        <span class="live-pill" data-camera-live hidden><i></i> camera live</span>
      </div>
      <p class="calibration-copy">Choose how the outward-facing phone is located in the TV frame. Both modes make their uncertainty visible.</p>
      <fieldset class="camera-install" data-camera-install>
        <legend>Under-TV installation</legend>
        <div class="camera-install-modes">
          <label><input type="radio" name="camera-install-mode" value="calibrated-offset" checked /> Calibrate, then move into stand</label>
          <label><input type="radio" name="camera-install-mode" value="declared" /> Declare bottom-centre mount</label>
        </div>
        <p class="installation-provenance" data-installation-provenance>${CAMERA_INSTALLATION_LABELS['calibrated-declared-offset']}</p>
        <div data-offset-install>
          <label>Stand move
            <select data-offset-kind>
              <option value="flip-in-place">Flip in place · 180° yaw</option>
              <option value="custom">Enter rigid offset</option>
            </select>
          </label>
          <div class="stand-offset-grid" data-custom-offset hidden>
            <label>x right <input data-offset-x type="number" step="0.001" value="0" /> m</label>
            <label>y down <input data-offset-y type="number" step="0.001" value="0" /> m</label>
            <label>z toward TV <input data-offset-z type="number" step="0.001" value="0" /> m</label>
            <label>pitch x <input data-offset-pitch type="number" step="0.1" value="0" />°</label>
            <label>yaw y <input data-offset-yaw type="number" step="0.1" value="180" />°</label>
            <label>roll z <input data-offset-roll type="number" step="0.1" value="0" />°</label>
          </div>
          <p>Freeze the phone while it faces the TV, tap its corners, then make exactly this physical move. Custom translation is the installed lens centre in the calibration-camera axes.</p>
        </div>
        <div data-declared-install hidden>
          <label>Camera / FOV profile
            <select data-phone-camera>
              ${PHONE_FRONT_CAMERAS.map((phone) => `<option value="${phone.id}">${phone.label}</option>`).join('')}
              <option value="custom">Custom horizontal FOV</option>
            </select>
          </label>
          <div data-custom-fov hidden><label>Horizontal FOV <input data-horizontal-fov type="number" min="20" max="150" step="0.1" value="${DEFAULT_CUSTOM_HORIZONTAL_FOV_DEG}" />°</label></div>
          <output data-phone-fov></output>
          <p>Manufacturer rows require a browser-verified front camera. Custom is the measured horizontal FOV of the selected lens. The lens is declared at the active picture's bottom centre, upright and facing into the room.</p>
          <button type="button" data-apply-declared disabled>Apply declared mount</button>
          <p data-installation-notice aria-live="polite"></p>
        </div>
      </fieldset>
      <div data-calibration-tv></div>
      <div class="calibration-view">
        <video data-calibration-video muted playsinline></video>
        <canvas data-calibration-canvas hidden></canvas>
      </div>
      <div class="calibration-actions">
        <button type="button" data-start-calibration>Prepare camera</button>
        <button type="button" data-freeze disabled>Freeze frame</button>
        <button type="button" data-reset disabled>Reset taps</button>
        <button type="button" data-stop disabled>Stop</button>
      </div>
      <section class="camera-frame-budget" data-camera-frame-budget>
        <div class="camera-frame-budget-heading">
          <div><p class="eyebrow">CAMERA FRAME BUDGET</p><b data-frame-budget>24 fps · 41.7 ms</b></div>
          <label>Target fps <select data-target-fps><option value="24">24</option><option value="15">15</option></select></label>
          <label>Packed matte <select data-packed-layout><option value="side-by-side">Side by side · full alpha</option><option value="top-bottom">Top / bottom · full alpha</option><option value="alpha-strip-half">Half-size alpha strip · +25%</option></select></label>
        </div>
        <div class="camera-frame-timings">
          <span>Pose <b data-inference-pose>—</b></span>
          <span>Segmentation <b data-inference-segmentation>—</b></span>
          <span>Mask analysis <b data-inference-mask>—</b></span>
          <span>Packing <b data-inference-packing>—</b></span>
          <span>Total <b data-inference-total>—</b></span>
        </div>
        <p data-frame-budget-notice aria-live="polite">Measured locally after Start; auto-steps 24 → 15 when p95 misses the budget.</p>
      </section>
      <p class="screen-heuristic-note">Screen hiding is a heuristic. It can miss screens or hide other bright rectangles.</p>
      <div data-camera-rtc-stats></div>
      <p class="pose-transport" data-pose-transport data-pose-source="blocked">Landmarks blocked until FLOOR selects a source.</p>
      <p class="calibration-status" data-calibration-status>Camera is off. Prepare opens only a local, black-output preview; Start grants the current person.</p>
    </section>`;

  const picker = container.querySelector<HTMLElement>('[data-calibration-tv]')!;
  const currentTv = mountTvPicker(picker);
  const video = container.querySelector<HTMLVideoElement>('[data-calibration-video]')!;
  const canvas = container.querySelector<HTMLCanvasElement>('[data-calibration-canvas]')!;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('2D canvas unavailable');
  const startButton = container.querySelector<HTMLButtonElement>('[data-start-calibration]')!;
  const freezeButton = container.querySelector<HTMLButtonElement>('[data-freeze]')!;
  const resetButton = container.querySelector<HTMLButtonElement>('[data-reset]')!;
  const stopButton = container.querySelector<HTMLButtonElement>('[data-stop]')!;
  const live = container.querySelector<HTMLElement>('[data-camera-live]')!;
  const status = container.querySelector<HTMLElement>('[data-calibration-status]')!;
  const performanceHost = container.querySelector<HTMLElement>('[data-camera-frame-budget]')!;
  const targetFpsSelect = container.querySelector<HTMLSelectElement>('[data-target-fps]')!;
  const packedLayoutSelect = container.querySelector<HTMLSelectElement>('[data-packed-layout]')!;
  const setRtcStats = mountRtcStatsHud(container.querySelector<HTMLElement>('[data-camera-rtc-stats]')!, 'Packed RTP · CAMERA outbound');
  const modeInputs = [...container.querySelectorAll<HTMLInputElement>('[name="camera-install-mode"]')];
  const provenance = container.querySelector<HTMLElement>('[data-installation-provenance]')!;
  const offsetInstall = container.querySelector<HTMLElement>('[data-offset-install]')!;
  const declaredInstall = container.querySelector<HTMLElement>('[data-declared-install]')!;
  const offsetKind = container.querySelector<HTMLSelectElement>('[data-offset-kind]')!;
  const customOffset = container.querySelector<HTMLElement>('[data-custom-offset]')!;
  const phoneCamera = container.querySelector<HTMLSelectElement>('[data-phone-camera]')!;
  const customFov = container.querySelector<HTMLElement>('[data-custom-fov]')!;
  const horizontalFov = container.querySelector<HTMLInputElement>('[data-horizontal-fov]')!;
  const phoneFovOutput = container.querySelector<HTMLOutputElement>('[data-phone-fov]')!;
  const applyDeclared = container.querySelector<HTMLButtonElement>('[data-apply-declared]')!;
  const installationNotice = container.querySelector<HTMLElement>('[data-installation-notice]')!;

  let stream: MediaStream | undefined;
  let frozenFrame: HTMLCanvasElement | undefined;
  let points: Point2[] = [];
  let grantReady = false;

  const markGrantReady = (): void => {
    if (!stream) return;
    grantReady = true;
    startButton.disabled = false;
    startButton.textContent = 'Start camera';
    status.textContent = 'Camera ready · press Start while alone, or tap your body in the preview.';
  };

  const setCameraStatus = (message: string): void => {
    status.textContent = message;
    if (message.includes('Paused: press Start to continue')) {
      grantReady = true;
      startButton.disabled = false;
      startButton.textContent = 'Start camera';
    } else if (message.startsWith('Camera live')) {
      grantReady = false;
      startButton.disabled = true;
    }
  };

  const currentMode = (): 'calibrated-offset' | 'declared' =>
    modeInputs.find((input) => input.checked)?.value === 'declared' ? 'declared' : 'calibrated-offset';

  const sendInstallation = (
    camera: InstalledCameraGeometry,
    image: { widthPx: number; heightPx: number },
    installation: CameraCalibrationMessage['installation'],
  ): void => {
    const tv = currentTv();
    const payload: CameraCalibrationMessage = {
      kind: 'camera-installation',
      tv: { id: tv.id, panelWidthM: tv.panelWidthM, panelHeightM: tv.panelHeightM },
      image,
      camera,
      installation,
      configuredAtMs: performance.timeOrigin + performance.now(),
    };
    hooks.onInstallation?.(payload);
    const position = camera.cameraPositionInTv;
    installationNotice.textContent = `Applied · ${installation.label}`;
    status.textContent = `Sent to FLOOR · ${installation.label} · f ${camera.focalLengthPx.toFixed(0)} px · camera (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}) m`;
  };

  const readNumber = (selector: string): number => Number(container.querySelector<HTMLInputElement>(selector)?.value);

  const selectedStandOffset = () => offsetKind.value === 'flip-in-place'
    ? flipInPlaceOffset()
    : declaredStandOffset(
      { x: readNumber('[data-offset-x]'), y: readNumber('[data-offset-y]'), z: readNumber('[data-offset-z]') },
      { pitchX: readNumber('[data-offset-pitch]'), yawY: readNumber('[data-offset-yaw]'), rollZ: readNumber('[data-offset-roll]') },
    );

  const updatePhoneFov = (): void => {
    const profile = PHONE_FRONT_CAMERAS.find((phone) => phone.id === phoneCamera.value);
    customFov.hidden = Boolean(profile);
    try {
      const fov = phoneHorizontalFov(phoneCamera.value, Number(horizontalFov.value));
      phoneFovOutput.value = profile
        ? `Published ${profile.publishedFovDeg.toFixed(1)}° (axis unstated) · treated as 4:3 diagonal → horizontal ${fov.toFixed(1)}° · approximate`
        : `Custom ${fov.toFixed(1)}° horizontal · approximate`;
    } catch (error) {
      phoneFovOutput.value = error instanceof Error ? error.message : 'Invalid field of view';
    }
  };

  const syncInstallMode = (): void => {
    const declared = currentMode() === 'declared';
    offsetInstall.hidden = declared;
    declaredInstall.hidden = !declared;
    provenance.textContent = declared
      ? CAMERA_INSTALLATION_LABELS['declared-approximate']
      : CAMERA_INSTALLATION_LABELS['calibrated-declared-offset'];
    freezeButton.disabled = declared || !stream;
    applyDeclared.disabled = !declared || !stream || video.videoWidth <= 0 || video.videoHeight <= 0;
    if (declared && frozenFrame) {
      video.hidden = false;
      canvas.hidden = true;
      frozenFrame = undefined;
      points = [];
    }
  };

  const setPerformance = (snapshot: FrameBudgetSnapshot): void => {
    targetFpsSelect.value = String(snapshot.targetFps);
    performanceHost.dataset.targetFps = String(snapshot.targetFps);
    performanceHost.dataset.inferenceSamples = String(snapshot.sampleCount);
    performanceHost.dataset.autoStepCount = String(snapshot.autoStepCount);
    performanceHost.dataset.overBudget = String(snapshot.overBudget);
    const budget = performanceHost.querySelector<HTMLElement>('[data-frame-budget]');
    if (budget) budget.textContent = `${snapshot.targetFps} fps · ${snapshot.budgetMs.toFixed(1)} ms budget`;
    for (const stage of ['pose', 'segmentation', 'mask', 'packing', 'total'] as const) {
      const stageSummary = snapshot.stages[stage];
      const output = performanceHost.querySelector<HTMLElement>(`[data-inference-${stage}]`);
      if (output) output.textContent = stageSummary.p50 === undefined
        ? '—'
        : `p50 ${stageSummary.p50.toFixed(1)} · p95 ${stageSummary.p95!.toFixed(1)} ms`;
      if (stageSummary.p50 !== undefined) performanceHost.dataset[`${stage}P50Ms`] = stageSummary.p50.toFixed(3);
      else delete performanceHost.dataset[`${stage}P50Ms`];
      if (stageSummary.p95 !== undefined) performanceHost.dataset[`${stage}P95Ms`] = stageSummary.p95.toFixed(3);
      else delete performanceHost.dataset[`${stage}P95Ms`];
    }
    const notice = performanceHost.querySelector<HTMLElement>('[data-frame-budget-notice]');
    if (notice && snapshot.notice) notice.textContent = snapshot.notice;
  };

  const redraw = (): void => {
    if (!frozenFrame) return;
    context.drawImage(frozenFrame, 0, 0);
    const radius = Math.max(6, canvas.width / 180);
    points.forEach((point, index) => {
      context.beginPath();
      context.arc(point.x, point.y, radius, 0, Math.PI * 2);
      context.fillStyle = '#f4c66d';
      context.fill();
      context.lineWidth = Math.max(2, radius / 4);
      context.strokeStyle = '#101314';
      context.stroke();
      context.fillStyle = '#101314';
      context.font = `700 ${Math.round(radius * 1.15)}px ui-monospace, monospace`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(String(index + 1), point.x, point.y);
    });
  };

  const updateTapStatus = (): void => {
    resetButton.disabled = points.length === 0;
    if (points.length < 4) {
      status.textContent = `Tap ${CORNER_NAMES[points.length]} of the visible picture (${points.length + 1}/4).`;
      return;
    }
    const tv = currentTv();
    try {
      const pose = solvePlanarTvPose(points as unknown as FourCorners, tv.panelWidthM, tv.panelHeightM, canvas.width, canvas.height);
      const camera = composeInstalledCamera(pose, selectedStandOffset());
      sendInstallation(camera, { widthPx: canvas.width, heightPx: canvas.height }, {
        mode: 'calibrated-offset',
        label: 'calibrated + declared offset',
        offsetKind: offsetKind.value === 'custom' ? 'custom' : 'flip-in-place',
        reprojectionRmsPx: pose.reprojectionRmsPx,
        viewObliquityDeg: pose.viewObliquityDeg,
      });
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : 'Calibration failed';
    }
  };

  const stop = (): void => {
    hooks.onStop?.();
    stream?.getTracks().forEach((track) => track.stop());
    stream = undefined;
    video.srcObject = null;
    video.hidden = false;
    canvas.hidden = true;
    frozenFrame = undefined;
    points = [];
    context.clearRect(0, 0, canvas.width, canvas.height);
    live.hidden = true;
    grantReady = false;
    startButton.disabled = false;
    startButton.textContent = 'Prepare camera';
    freezeButton.disabled = true;
    applyDeclared.disabled = true;
    resetButton.disabled = true;
    stopButton.disabled = true;
    packedLayoutSelect.disabled = false;
    status.textContent = 'Camera stopped. No frame or pose was stored.';
  };

  startButton.addEventListener('click', () => {
    if (stream) {
      if (!grantReady) return;
      grantReady = false;
      startButton.disabled = true;
      hooks.onGrantRequest?.();
      status.textContent = 'Start pressed · binding the only person in the current frame.';
      return;
    }
    startButton.disabled = true;
    packedLayoutSelect.disabled = true;
    status.textContent = 'Waiting for camera permission…';
    void navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { width: { ideal: 1920 }, height: { ideal: 1080 }, facingMode: { ideal: 'user' } },
    }).then(async (opened) => {
      stream = opened;
      video.srcObject = opened;
      await video.play();
      live.hidden = false;
      stopButton.disabled = false;
      syncInstallMode();
      status.textContent = currentMode() === 'declared'
        ? 'Camera ready. Apply the declared bottom-centre mount.'
        : 'Aim at the complete visible TV picture, then freeze a frame.';
      hooks.onStreamStarted?.(
        opened,
        video,
        setCameraStatus,
        (diagnostics) => {
          status.dataset.posePersonCount = String(diagnostics.posePersonCount);
          status.dataset.maskPersonCount = String(diagnostics.maskPersonCount);
        },
        setPerformance,
        setRtcStats,
        setRtcStats.setPublishSettings,
        Number(targetFpsSelect.value) as TargetFps,
        packedLayoutSelect.value as PackedLayout,
        markGrantReady,
      );
    }).catch((error: unknown) => {
      startButton.disabled = false;
      packedLayoutSelect.disabled = false;
      status.textContent = error instanceof Error ? error.message : 'Camera permission failed';
    });
  });

  freezeButton.addEventListener('click', () => {
    if (currentMode() !== 'calibrated-offset' || !stream || video.videoWidth === 0 || video.videoHeight === 0) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    frozenFrame = document.createElement('canvas');
    frozenFrame.width = canvas.width;
    frozenFrame.height = canvas.height;
    frozenFrame.getContext('2d', { alpha: false })?.drawImage(video, 0, 0);
    points = [];
    video.hidden = true;
    canvas.hidden = false;
    redraw();
    updateTapStatus();
  });

  resetButton.addEventListener('click', () => {
    points = [];
    redraw();
    updateTapStatus();
  });

  applyDeclared.addEventListener('click', () => {
    if (!stream || video.videoWidth <= 0 || video.videoHeight <= 0) {
      status.textContent = 'Start the camera before applying a declared mount.';
      return;
    }
    try {
      const facingMode = stream.getVideoTracks()[0]?.getSettings().facingMode;
      if (!phoneProfileMatchesFacingMode(phoneCamera.value, facingMode)) {
        throw new Error('Browser did not verify the front camera for this manufacturer profile; select Custom and enter the selected lens FOV');
      }
      const tv = currentTv();
      const fov = phoneHorizontalFov(phoneCamera.value, Number(horizontalFov.value));
      const camera = declaredBottomCentreCamera(tv.panelHeightM, video.videoWidth, video.videoHeight, fov);
      sendInstallation(camera, { widthPx: video.videoWidth, heightPx: video.videoHeight }, {
        mode: 'declared',
        label: 'declared, approximate',
        phoneProfileId: phoneCamera.value,
        horizontalFovDeg: fov,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not declare camera installation';
      installationNotice.textContent = message;
      status.textContent = message;
    }
  });

  modeInputs.forEach((input) => input.addEventListener('change', syncInstallMode));
  offsetKind.addEventListener('change', () => { customOffset.hidden = offsetKind.value !== 'custom'; });
  phoneCamera.addEventListener('change', () => {
    installationNotice.textContent = '';
    updatePhoneFov();
  });
  horizontalFov.addEventListener('input', updatePhoneFov);
  updatePhoneFov();
  syncInstallMode();

  stopButton.addEventListener('click', stop);
  targetFpsSelect.addEventListener('change', () => {
    const value = Number(targetFpsSelect.value);
    if (!isTargetFps(value)) return;
    const budget = performanceHost.querySelector<HTMLElement>('[data-frame-budget]');
    if (budget) budget.textContent = `${value} fps · ${frameBudgetMs(value).toFixed(1)} ms budget`;
    performanceHost.dataset.targetFps = String(value);
    hooks.onTargetFpsChange?.(value);
  });
  video.addEventListener('pointerdown', (event) => {
    if (!stream || !grantReady || video.hidden || video.videoWidth <= 0 || video.videoHeight <= 0) return;
    const bounds = video.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    const tap = {
      x: Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)),
      y: Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height)),
    };
    grantReady = false;
    startButton.disabled = true;
    hooks.onGrantRequest?.(tap);
    status.textContent = 'Start pressed · binding the person nearest this tap.';
  });
  canvas.addEventListener('pointerdown', (event) => {
    if (!frozenFrame || points.length >= 4) return;
    const bounds = canvas.getBoundingClientRect();
    points.push({
      x: ((event.clientX - bounds.left) / bounds.width) * canvas.width,
      y: ((event.clientY - bounds.top) / bounds.height) * canvas.height,
    });
    redraw();
    updateTapStatus();
  });

  return { stop };
}
